import { createContext, useContext, useEffect, useReducer } from 'react';
import { PaletteMetrics } from './Palette';
import { RequireInviteCode } from './InviteForm';
import { SlateIconWidth } from './CleanSlate';

export type WindowID =
  | 'colors'
  | 'codi'
  | 'typography'
  | 'screens'
  | 'invite-code'
  | 'quest'
  | 'versions'
  | 'auth'
  | 'code'
  | 'header'
  | 'version-history'
  | 'codi-tip'
  | 'components'
  | 'connect'
  | 'launch'
  | 'json-import'
  | 'component-preview-debug'
  | 'subscriptions'
  | 'live-preview';

export type Position =
  | {
      type: 'absolute';
      x: number;
      y: number;
    }
  | {
      type: 'after';
      otherID: WindowID;
    }
  | { type: 'before'; otherID: WindowID }
  | {
      type: 'relative';
      x: Relation;
      y: Relation;
      yOffset?: number;
    };

export type ResolvedPosition = {
  type: 'absolute';
  x: number;
  y: number;
};

const GapForAfterWindowPosition = 16;

function resolvePosition({
  id,
  unresolved,
  state,
}: {
  id: WindowID;
  unresolved: Position;
  state: WindowState;
}): ResolvedPosition | null {
  switch (unresolved.type) {
    case 'absolute': {
      return {
        type: 'absolute',
        x: unresolved.x,
        y: unresolved.y,
      };
    }
    case 'after': {
      const resolvedOther = resolvedPosition(unresolved.otherID, state);
      if (resolvedOther === null) {
        return null;
      }

      const otherSize = state.windows[unresolved.otherID]?.size?.width;
      if (otherSize === undefined) {
        return null;
      }

      const resolvedCodiTip = resolvedPosition('codi-tip', state);
      if (resolvedCodiTip === null) {
        return null;
      }

      // The reported size of palettes is 40 units smaller than the actual width, so adjust the size before positioning
      const adjustedWidth = otherSize - 40;

      return {
        type: 'absolute',
        x: resolvedOther.x + adjustedWidth + GapForAfterWindowPosition,
        y: resolvedCodiTip.y,
      };
    }
    case 'before': {
      const resolvedOther = resolvedPosition(unresolved.otherID, state);

      if (resolvedOther === null) {
        return null;
      }

      const selfSize = state.windows[id]?.size?.width;
      if (selfSize === undefined) {
        return null;
      }

      const resolvedCodiTip = resolvedPosition('codi-tip', state);
      if (resolvedCodiTip === null) {
        return null;
      }

      return {
        type: 'absolute',
        x: resolvedOther.x - (selfSize + GapForAfterWindowPosition),
        y: resolvedCodiTip.y,
      };
    }
    case 'relative': {
      const width = state.windows[id]?.size?.width;
      if (width === undefined) {
        console.log('No width for window id: ', id);
        return null;
      }
      const height = state.windows[id]?.size?.height;

      const { innerWidth, innerHeight } = window;

      function resolveY(
        unresolvedY: Relation,
        yOffset: number,
      ): number | undefined {
        if (unresolvedY === 'center') {
          if (height === undefined) {
            return undefined;
          }

          const calculatedY = innerHeight / 2 - height / 2;

          return calculatedY + yOffset;
        }

        return unresolvedY;
      }

      const y = resolveY(unresolved.y, unresolved.yOffset ?? 0);
      if (y === undefined) {
        console.log('Could not resolve y position for window id: ', id);
        return null;
      }

      return {
        type: 'absolute',
        x:
          unresolved.x === 'center' ? innerWidth / 2 - width / 2 : unresolved.x,
        y,
      };
    }
  }
}

export function resolvedPosition(
  id: WindowID,
  state: WindowState,
): ResolvedPosition | null {
  const unresolved = state.windows[id]?.position;
  if (unresolved === undefined) {
    return null;
  }

  return resolvePosition({
    id,
    unresolved,
    state,
  });
}

export function useResolvedPosition(id: WindowID): ResolvedPosition | null {
  const { state } = useWindows();

  return resolvedPosition(id, state);
}

type Size = {
  width: number;
  height?: number;
  maxHeight?: number;
};

export type WindowOptions = {
  fixed?: boolean;
};

export type Window = {
  position: Position;
  isHidden?: boolean;
  size?: Size;
  options?: WindowOptions;
};

// Indicates where windows positioned relative to the slate should be placed
export type SlatePositionMode = 'inside' | 'outside';
export type WindowState = {
  slatePosition: SlatePositionMode;
  windows: { [windowID in WindowID]: Window };
  resolutionCount: number;
};

type Relation = 'center' | number;

function updateWindow(
  state: WindowState,
  id: WindowID,
  updater: (window: Window) => Window,
  incrementResolution = false,
): WindowState {
  const existingWindow = state.windows[id];

  const updatedWindow = updater(existingWindow);
  const resolutionCount = incrementResolution
    ? state.resolutionCount + 1
    : state.resolutionCount;

  return {
    ...state,
    windows: {
      ...state.windows,
      [id]: updatedWindow,
    },
    resolutionCount,
  };
}

const PaletteYOffset = 164;

const DefaultWindowState: WindowState = {
  slatePosition: 'inside',
  windows: {
    subscriptions: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 620 },
    },
    colors: { position: { type: 'absolute', x: 20, y: PaletteYOffset } },
    codi: {
      position: { type: 'relative', x: 'center', y: 'center' },
      size: { width: 320, height: 155 },
      options: { fixed: true },
    },
    typography: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
    },
    screens: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 480 },
    },
    'invite-code': {
      position: { type: 'relative', x: 'center', y: PaletteYOffset },
      size: { width: 380 },
      options: { fixed: true },
    },
    quest: {
      position: { type: 'relative', x: 'center', y: 20 },
      size: { width: 320 },
      options: { fixed: true },
    },
    versions: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
    },
    auth: {
      position: { type: 'relative', x: 'center', y: PaletteYOffset },
      size: { width: 320 },
      isHidden: RequireInviteCode,
    },
    code: {
      position: { type: 'after', otherID: 'codi' },
      size: { width: 480 },
      isHidden: true,
    },
    header: {
      position: { type: 'relative', x: 'center', y: 100 },
      size: { width: 320 },
      options: { fixed: true },
    },
    'version-history': {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
    },
    'codi-tip': {
      position: {
        type: 'absolute',
        // Magic numbers to place aligned with Codify logo
        x: 30,
        y: 100,
      },
      size: { width: 176 },
      options: { fixed: true },
    },
    components: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
      options: { fixed: true },
    },
    connect: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
      options: { fixed: true },
    },
    launch: {
      position: { type: 'absolute', x: 20, y: PaletteYOffset },
      size: { width: 320 },
      options: { fixed: true },
    },
    'json-import': {
      position: { type: 'absolute', x: 1177, y: 400 },
      size: { width: 480 },
    },
    'component-preview-debug': {
      position: { type: 'absolute', x: 1177, y: 750 },
      size: { width: 480 },
    },
    'live-preview': {
      position: { type: 'relative', x: 'center', y: 'center' },
      options: { fixed: true },
    },
  },
  resolutionCount: 0,
};

export type WindowAction =
  | {
      type: 'setSize';
      id: WindowID;
      size: Size;
      requiredResolutionCount?: number;
    }
  | { type: 'setPosition'; id: WindowID; position: Position }
  | { type: 'hide'; id: WindowID }
  | { type: 'show'; id: WindowID }
  | { type: 'move-codi'; id: Exclude<WindowID, 'codi'> }
  | { type: 'center'; id: WindowID }
  | { type: 'replace-windows'; windows: WindowState['windows'] }
  | { type: 'mark-fixed'; id: WindowID }
  | { type: 'mark-unfixed'; id: WindowID }
  | { type: 'hide-all'; except?: WindowID[] }
  | { type: 'recalculate-positions' };
type WindowDispatch = (_: WindowAction) => void;

type WindowContextProps = { state: WindowState; dispatch: WindowDispatch };

const WindowContext = createContext<WindowContextProps | null>(null);
export function useWindows() {
  const context = useContext(WindowContext);
  if (context === null) {
    throw new Error('useWindows must be used within a WindowBoundary');
  }

  return context;
}

export function usePosition(id: WindowID): Position | undefined {
  const { state } = useWindows();
  return state.windows[id]?.position;
}

export function WindowBoundary({ children }: React.PropsWithChildren) {
  function reducer(state: WindowState, action: WindowAction): WindowState {
    switch (action.type) {
      case 'setSize': {
        // The given size is passed by calculated the width of the content, but the palette itself has some padding - so add that padding.
        const contentWidth = action.size.width;
        const palettePadding = PaletteMetrics.padding;
        const windowWidth = contentWidth + palettePadding * 2;

        if (
          action.requiredResolutionCount !== undefined &&
          action.requiredResolutionCount !== state.resolutionCount
        ) {
          console.log('Ignoring set size');
          return state;
        }

        return updateWindow(
          state,
          action.id,
          (window) => ({
            ...window,
            size: {
              ...window.size,
              width: windowWidth,
              height: action.size.height,
              maxHeight: action.size.maxHeight,
            },
          }),
          true,
        );
      }
      case 'setPosition': {
        return updateWindow(
          state,
          action.id,
          (window) => ({ ...window, position: action.position }),
          true,
        );
      }
      case 'show': {
        return updateWindow(state, action.id, (window) => ({
          ...window,
          isHidden: false,
        }));
      }
      case 'hide': {
        return updateWindow(state, action.id, (window) => ({
          ...window,
          isHidden: true,
        }));
      }
      case 'move-codi': {
        const codiWindow = state.windows.codi;
        if (codiWindow === undefined) {
          return state;
        }

        return {
          ...state,
          windows: {
            ...state.windows,
            codi: {
              ...codiWindow,
              position: {
                type: 'after',
                otherID: action.id,
              },
            },
          },
          resolutionCount: state.resolutionCount + 1,
        };
      }
      case 'center': {
        const { innerWidth } = globalThis.window;
        const window = state.windows[action.id];

        // No-op for relative positioned windows
        if (window.position.type === 'relative') {
          return state;
        }

        // Helper to create updated state with incremented resolution count
        const createCenteredState = (
          windowUpdates: Partial<Record<WindowID, Window>>,
        ) => ({
          ...state,
          windows: { ...state.windows, ...windowUpdates },
          resolutionCount: state.resolutionCount + 1,
        });

        if (window.position.type === 'absolute') {
          const width = window.size?.width ?? 375;
          return createCenteredState({
            [action.id]: {
              ...window,
              position: {
                type: 'absolute',
                x: (innerWidth - width) / 2,
                y: window.position.y,
              },
            },
          });
        }

        // Handle 'after' position type
        const selfWidth = window.size?.width ?? 365;
        const other = state.windows[window.position.otherID];
        const otherWidth = other.size?.width ?? 355;
        const totalWidth = selfWidth + otherWidth + GapForAfterWindowPosition;
        const y =
          other.position.type === 'absolute'
            ? other.position.y
            : PaletteYOffset;

        return createCenteredState({
          [window.position.otherID]: {
            ...other,
            position: {
              type: 'absolute',
              x: (innerWidth - totalWidth) / 2,
              y,
            },
          },
        });
      }
      case 'replace-windows': {
        return {
          ...state,
          windows: action.windows,
        };
      }
      case 'mark-fixed': {
        return updateWindow(state, action.id, (window) => {
          const existingOptions = window.options ?? {};
          return {
            ...window,
            options: {
              ...existingOptions,
              fixed: true,
            },
          };
        });
      }
      case 'mark-unfixed': {
        return updateWindow(state, action.id, (window) => {
          const existingOptions = window.options ?? {};
          return {
            ...window,
            options: {
              ...existingOptions,
              fixed: false,
            },
          };
        });
      }
      case 'hide-all': {
        const newWindows = state.windows;
        Object.keys(state.windows).forEach((windowID) => {
          const windowIDsToNotHide = action.except ?? [];
          const wID = windowID as WindowID;
          const isHidden = !windowIDsToNotHide.includes(wID);
          newWindows[wID] = {
            ...newWindows[wID],
            isHidden,
          };
        });
        return {
          ...state,
          windows: newWindows,
        };
      }
      case 'recalculate-positions': {
        // TODO: Implement
        return {
          ...state,
          resolutionCount: state.resolutionCount + 1,
        };
      }
    }
  }
  const [state, dispatch] = useReducer(reducer, DefaultWindowState);

  useEffect(
    function listenForWindowResizing() {
      function handleResize() {
        dispatch({ type: 'recalculate-positions' });
      }

      window.addEventListener('resize', handleResize);

      return function cleanup() {
        window.removeEventListener('resize', handleResize);
      };
    },
    [dispatch],
  );

  return (
    <WindowContext.Provider value={{ state, dispatch }}>
      {children}
    </WindowContext.Provider>
  );
}
